import {
  regionKey,
  type MacroCaptureResult,
  type MacroWalkResponsePayload,
} from '@playcastdotio/Messaging';
import type { IPlayjectorService, MacroStepCaptureSpec, MacroStepCaptureResult } from '../../../services/playjector/interfaces/IPlayjectorService';
import type { MacroManager } from '../../game-launch/managers/MacroManager';
import { GuestFrameTracker } from './GuestFrameTracker';
import * as Sentry from '@sentry/react';

function waitMs(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export interface MacroWalkStepExecutorDeps {
  macroManager: MacroManager;
  playjectorService: IPlayjectorService;
  guestFrameTracker: GuestFrameTracker;
  getLaunchInstanceId(): string | null;
  isCurrentLaunch(launchInstanceId: string): boolean;
  sendWalkRequest(args: {
    campaignId: string;
    trigger: MacroWalkResponsePayload['trigger'];
    launchInstanceId: string;
    currentNodeId: string | null;
    captures: MacroCaptureResult[];
    waitMatchCurrent?: boolean;
  }): void;
  updateCurrentNode(nextNodeId: string | null): void;
  fallbackToLegacy(reason: string): void;
  setSettling(active: boolean): void;
  getActiveAndroidStreamId?: () => string | undefined;
  getLiveGameInstanceIndices?: () => number[];
  requestStepCapturesForLaunch: (
    captures: Record<string, MacroStepCaptureSpec>,
    launchInstanceId: string,
  ) => Promise<Record<string, MacroStepCaptureResult> | 'stale'>;
}

/**
 * Continue-outcome execution: macro playback, settle delay, and capture requests.
 */
export class MacroWalkStepExecutor {
  constructor(private readonly deps: MacroWalkStepExecutorDeps) {}

  public async executeContinue(payload: MacroWalkResponsePayload): Promise<void> {
    console.log('[StatefulMacroSystemStep] 🟢 step (non-leaf) — running macros + requesting captures');

    try {
      await this.runWalkMacros(payload, 'StatefulMacro');
    } catch (err) {
      console.error('[StatefulMacroSystemStep] runMacroSteps threw on continue path:', {
        campaignId: payload.campaignId,
        launchInstanceId: payload.launchInstanceId,
        nextNodeId: payload.nextNodeId,
        macroCount: payload.macros.length,
        error: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : undefined,
      });
      this.deps.fallbackToLegacy('runMacroSteps_threw');
      return;
    }

    if (!this.deps.isCurrentLaunch(payload.launchInstanceId)) {
      console.warn('[StatefulMacroSystemStep] Launch instance changed during runMacroSteps — abandoning step', {
        payloadLaunchInstanceId: payload.launchInstanceId,
        currentLaunchInstanceId: this.deps.getLaunchInstanceId(),
      });
      return;
    }

    const delayMs = payload.postMacroDelayMs ?? 0;
    if (delayMs > 0) {
      console.log('[StatefulMacroSystemStep] ⏳ Waiting postMacroDelayMs before requesting capture', {
        campaignId: payload.campaignId,
        launchInstanceId: payload.launchInstanceId,
        nextNodeId: payload.nextNodeId,
        postMacroDelayMs: delayMs,
      });
      this.deps.setSettling(true);
      await waitMs(delayMs);
      this.deps.setSettling(false);

      if (!this.deps.isCurrentLaunch(payload.launchInstanceId)) {
        console.warn('[StatefulMacroSystemStep] Launch instance changed during postMacroDelayMs — abandoning step', {
          payloadLaunchInstanceId: payload.launchInstanceId,
          currentLaunchInstanceId: this.deps.getLaunchInstanceId(),
        });
        return;
      }
    }

    if (payload.captures.length === 0) {
      console.log('[StatefulMacroSystemStep] 📸 Continue with empty captures — screenshot-optional node, skipping requestMacroStep', {
        campaignId: payload.campaignId,
        launchInstanceId: payload.launchInstanceId,
        nextNodeId: payload.nextNodeId,
      });

      this.deps.updateCurrentNode(payload.nextNodeId);
      this.deps.sendWalkRequest({
        campaignId: payload.campaignId,
        trigger: payload.trigger,
        launchInstanceId: payload.launchInstanceId,
        currentNodeId: payload.nextNodeId,
        captures: [],
      });
      return;
    }

    const capturesByCaptureId = this.deps.guestFrameTracker.buildCaptureSpecs(payload);

    let hashes: Record<string, MacroStepCaptureResult> | 'stale';
    try {
      hashes = await this.deps.requestStepCapturesForLaunch(capturesByCaptureId, payload.launchInstanceId);
    } catch (err) {
      console.error('[StatefulMacroSystemStep] requestMacroStep (Playjector IPC) failed:', {
        campaignId: payload.campaignId,
        launchInstanceId: payload.launchInstanceId,
        captureCount: payload.captures.length,
        captureIds: Object.keys(capturesByCaptureId),
        error: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : undefined,
      });
      this.deps.fallbackToLegacy('requestMacroStep_failed');
      return;
    }
    if (hashes === 'stale') return;

    this.deps.guestFrameTracker.rememberGuestFrameFromHashes(hashes);

    if (!this.deps.isCurrentLaunch(payload.launchInstanceId)) {
      console.warn('[StatefulMacroSystemStep] Launch instance changed mid-step — dropping captured hashes', {
        payloadLaunchInstanceId: payload.launchInstanceId,
        currentLaunchInstanceId: this.deps.getLaunchInstanceId(),
      });
      return;
    }

    this.deps.updateCurrentNode(payload.nextNodeId);

    const nextCaptures: MacroCaptureResult[] = Object.entries(hashes).map(([captureId, result]) => {
      const spec = capturesByCaptureId[captureId];
      const capture: MacroCaptureResult = {
        captureId,
        hash: result.hash,
        width: result.width,
        height: result.height,
      };
      if (spec && regionKey(spec.region) !== '') {
        capture.region = spec.region;
      }
      return capture;
    });

    this.deps.sendWalkRequest({
      campaignId: payload.campaignId,
      trigger: payload.trigger,
      launchInstanceId: payload.launchInstanceId,
      currentNodeId: payload.nextNodeId,
      captures: nextCaptures,
    });
  }

  public async executeComplete(payload: MacroWalkResponsePayload): Promise<void> {
    console.log('[StatefulMacroSystemStep] 🏁 step (leaf) — walk complete');

    if (payload.macros.length > 0) {
      try {
        await this.runWalkMacros(payload, 'StatefulMacro:leaf');
      } catch (err) {
        console.error('[StatefulMacroSystemStep] runMacroSteps threw on complete path:', err);
      }
    }

    if (!this.deps.isCurrentLaunch(payload.launchInstanceId)) {
      console.warn('[StatefulMacroSystemStep] Launch instance changed during leaf runMacroSteps — skipping walk-complete signal', {
        payloadLaunchInstanceId: payload.launchInstanceId,
        currentLaunchInstanceId: this.deps.getLaunchInstanceId(),
      });
    }
  }

  public async runWalkMacros(payload: MacroWalkResponsePayload, label: string): Promise<void> {
    const frame = this.deps.guestFrameTracker.frameForPlayback(payload);
    const resolution = this.deps.guestFrameTracker.resolve(payload);
    const mouseSteps = payload.macros.filter((step) => step.trim().startsWith('mouse='));
    if (mouseSteps.length > 0) {
      const mouseContext = {
        label,
        campaignId: payload.campaignId,
        launchInstanceId: payload.launchInstanceId,
        nextNodeId: payload.nextNodeId,
        width: frame?.width ?? null,
        height: frame?.height ?? null,
        captureCount: payload.captures.length,
        frameResolution: resolution.kind,
        usedCache: resolution.kind === 'resolved' && payload.captures.length === 0,
        mouseSteps,
        absCount: mouseSteps.filter((step) => step.startsWith('mouse=abs')).length,
      };
      console.log('[StatefulMacroSystemStep] Mouse playback frame', mouseContext);
      Sentry.logger.info(
        `[StatefulMacroSystemStep] Mouse playback frame: ${label} node=${payload.nextNodeId ?? 'none'} `
          + `frame=${frame ? `${frame.width}x${frame.height}` : 'none'} `
          + `resolution=${resolution.kind} absCount=${mouseContext.absCount} `
          + `steps=[${mouseSteps.join(' | ')}]`,
      );
    }
    await this.deps.macroManager.runMacroSteps(payload.macros, label, frame);
  }
}
